import React, { useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';
import { updateSetting } from '../../../store/settingsSlice';
import { v4 as uuidv4 } from 'uuid';
import ConfirmModal from '../../common/ConfirmModal';
import IconSelector from '../../Projects/IconSelector';

const defaultFavoriteIcons = ['work', 'pets', 'science', 'bloodtype', 'pill', 'people', 'circle', 'family_home'];

const selectSettingsState = state => state.settings.settings;

const selectFavoriteIcons = createSelector(
    [selectSettingsState],
    (settings) => {
        return settings.favorite_icons ? [...settings.favorite_icons] : [...defaultFavoriteIcons];
    }
);

const ProjectTemplates = () => {
    const dispatch = useDispatch();
    const templatesFromState = useSelector(state => state.settings.settings.projectTemplates);
    const templates = useMemo(() => templatesFromState || [], [templatesFromState]);
    const favoriteIcons = useSelector(selectFavoriteIcons);
    
    const [isCreating, setIsCreating] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        icon: 'folder',
        status: 'NOT_STARTED',
        todos: ''
    });
    const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, templateId: null, templateName: '' });
    const [validationError, setValidationError] = useState({ isOpen: false, message: '' });
    const [anchorEl, setAnchorEl] = useState(null);

    const PROJECT_STATUSES = [
        { value: 'NOT_STARTED', label: 'Not Started' },
        { value: 'IN_REVIEW', label: 'In Review' },
        { value: 'IN_PROGRESS', label: 'In Progress' },
        { value: 'READY_FOR_GOLIVE', label: 'Ready for Go-Live' },
        { value: 'COMPLETED', label: 'Completed' }
    ];

    const handleStartCreate = () => {
        setIsCreating(true);
        setEditingId(null);
        setFormData({
            name: '',
            icon: 'folder',
            status: 'NOT_STARTED',
            todos: ''
        });
    };

    const handleStartEdit = (template) => {
        setEditingId(template.id);
        setIsCreating(false);
        setFormData({
            name: template.name,
            icon: template.icon,
            status: template.status,
            todos: template.todos.join('\n')
        });
    };

    const handleCancel = () => {
        setIsCreating(false);
        setEditingId(null);
        setFormData({
            name: '',
            icon: 'folder',
            status: 'NOT_STARTED',
            todos: ''
        });
    };

    const handleSave = async () => {
        if (!formData.name.trim()) {
            setValidationError({ 
                isOpen: true, 
                message: 'Please enter a template name before saving.' 
            });
            return;
        }

        const todos = formData.todos
            .split('\n')
            .map(todo => todo.trim())
            .filter(todo => todo.length > 0);

        let newTemplates;
        if (editingId) {
            // Update existing template
            newTemplates = templates.map(t => 
                t.id === editingId 
                    ? { ...t, ...formData, todos }
                    : t
            );
        } else {
            // Create new template
            const newTemplate = {
                id: uuidv4(),
                name: formData.name.trim(),
                icon: formData.icon,
                status: formData.status,
                todos
            };
            newTemplates = [...templates, newTemplate];
        }

        await dispatch(updateSetting({ 
            key: 'projectTemplates', 
            value: newTemplates 
        }));

        handleCancel();
    };

    const handleDeleteClick = (id, name) => {
        setDeleteConfirm({ isOpen: true, templateId: id, templateName: name });
    };

    const handleDeleteConfirm = async () => {
        const newTemplates = templates.filter(t => t.id !== deleteConfirm.templateId);
        await dispatch(updateSetting({ 
            key: 'projectTemplates', 
            value: newTemplates 
        }));
    };

    const handleOpenIconSelector = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const handleCloseIconSelector = () => {
        setAnchorEl(null);
    };

    const handleSelectIcon = (icon) => {
        setFormData({ ...formData, icon });
        handleCloseIconSelector();
    };

    return (
        <div className="p-6 space-y-4">
            <div className="mb-6">
                <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-2">
                    Project Templates
                </h3>
                <p className="text-sm text-[var(--text-muted)]">
                    Create reusable project templates with preset todos. When creating a new project, 
                    you can choose a template to automatically add predefined tasks.
                </p>
            </div>

            {/* Create/Edit Form */}
            {(isCreating || editingId) && (
                <div className="p-4 bg-[var(--input-bg)] border border-[var(--input-border)] rounded-lg space-y-4">
                    <h4 className="font-medium text-[var(--text-primary)]">
                        {editingId ? 'Edit Template' : 'Create New Template'}
                    </h4>
                    
                    {/* Template Name */}
                    <div>
                        <label className="block text-sm font-medium text-[var(--text-primary)] mb-1">
                            Template Name *
                        </label>
                        <input
                            type="text"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            placeholder="e.g., Website Project, Marketing Campaign"
                            className="w-full p-2 bg-[var(--surface-bg)] border border-[var(--input-border)] rounded-md text-[var(--text-primary)] focus:outline-none focus:border-[var(--input-border-hover)]"
                        />
                    </div>

                    {/* Default Icon */}
                    <div>
                        <label className="block text-sm font-medium text-[var(--text-primary)] mb-1">
                            Default Icon
                        </label>
                        <div className="flex flex-wrap gap-2">
                            {favoriteIcons.map(icon => (
                                <button
                                    key={icon}
                                    onClick={() => setFormData({ ...formData, icon })}
                                    className={`p-2 rounded-md transition-colors ${
                                        formData.icon === icon
                                            ? 'bg-[var(--selection-bg)] text-[var(--text-primary)]'
                                            : 'bg-[var(--surface-bg)] text-[var(--text-muted)] hover:bg-[var(--button-hover-bg)]'
                                    }`}
                                    title={icon}
                                >
                                    <span className="material-symbols-outlined text-[20px]">{icon}</span>
                                </button>
                            ))}
                            {/* Show selected icon if it's not in favorites */}
                            {formData.icon && !favoriteIcons.includes(formData.icon) && (
                                <button
                                    onClick={() => setFormData({ ...formData, icon: formData.icon })}
                                    className="p-2 rounded-md bg-[var(--selection-bg)] text-[var(--text-primary)] transition-colors border-2 border-blue-500"
                                    title={`${formData.icon} (selected)`}
                                >
                                    <span className="material-symbols-outlined text-[20px]">{formData.icon}</span>
                                </button>
                            )}
                            <button
                                onClick={handleOpenIconSelector}
                                className="p-2 rounded-md bg-[var(--surface-bg)] text-[var(--text-muted)] hover:bg-[var(--button-hover-bg)] transition-colors"
                                title="Choose another icon"
                                type="button"
                            >
                                <span className="material-symbols-outlined text-[20px]">add</span>
                            </button>
                        </div>
                    </div>

                    {/* Default Status */}
                    <div>
                        <label className="block text-sm font-medium text-[var(--text-primary)] mb-1">
                            Default Status
                        </label>
                        <select
                            value={formData.status}
                            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                            className="w-full p-2 bg-[var(--surface-bg)] border border-[var(--input-border)] rounded-md text-[var(--text-primary)] focus:outline-none focus:border-[var(--input-border-hover)]"
                        >
                            {PROJECT_STATUSES.map(status => (
                                <option key={status.value} value={status.value}>
                                    {status.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Preset Todos */}
                    <div>
                        <label className="block text-sm font-medium text-[var(--text-primary)] mb-1">
                            Preset Todos (one per line)
                        </label>
                        <textarea
                            value={formData.todos}
                            onChange={(e) => setFormData({ ...formData, todos: e.target.value })}
                            placeholder="Setup project repository&#10;Create initial documentation&#10;Configure development environment&#10;Setup CI/CD pipeline"
                            rows={8}
                            className="w-full p-2 bg-[var(--surface-bg)] border border-[var(--input-border)] rounded-md text-[var(--text-primary)] focus:outline-none focus:border-[var(--input-border-hover)] font-mono text-sm"
                        />
                        <p className="text-xs text-[var(--text-muted)] mt-1">
                            Enter each todo on a new line. These will be added when a project is created from this template.
                        </p>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-2 justify-end">
                        <button
                            onClick={handleCancel}
                            className="px-4 py-2 bg-[var(--surface-bg)] text-[var(--text-primary)] rounded-md hover:bg-[var(--button-hover-bg)] transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSave}
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                        >
                            {editingId ? 'Update Template' : 'Create Template'}
                        </button>
                    </div>
                </div>
            )}

            {/* Add New Button */}
            {!isCreating && !editingId && (
                <button
                    onClick={handleStartCreate}
                    className="w-full p-3 border-2 border-dashed border-[var(--input-border)] rounded-lg text-[var(--text-muted)] hover:border-[var(--input-border-hover)] hover:text-[var(--text-primary)] transition-colors flex items-center justify-center gap-2"
                >
                    <span className="material-symbols-outlined">add</span>
                    Create New Template
                </button>
            )}

            {/* Templates List */}
            {templates.length > 0 && !isCreating && !editingId && (
                <div className="space-y-3 mt-6">
                    <h4 className="font-medium text-[var(--text-primary)]">
                        Your Templates ({templates.length})
                    </h4>
                    {templates.map(template => (
                        <div
                            key={template.id}
                            className="p-4 bg-[var(--input-bg)] border border-[var(--input-border)] rounded-lg hover:border-[var(--input-border-hover)] transition-colors"
                        >
                            <div className="flex items-start justify-between">
                                <div className="flex items-start gap-3 flex-1">
                                    <span className="material-symbols-outlined text-[24px] text-[var(--text-primary)]">
                                        {template.icon}
                                    </span>
                                    <div className="flex-1">
                                        <h5 className="font-medium text-[var(--text-primary)] mb-1">
                                            {template.name}
                                        </h5>
                                        <p className="text-sm text-[var(--text-muted)] mb-2">
                                            Status: {PROJECT_STATUSES.find(s => s.value === template.status)?.label || template.status}
                                        </p>
                                        {template.todos.length > 0 && (
                                            <div className="text-sm text-[var(--text-muted)]">
                                                <span className="font-medium">{template.todos.length} preset todo{template.todos.length !== 1 ? 's' : ''}:</span>
                                                <ul className="list-disc list-inside mt-1 space-y-1">
                                                    {template.todos.slice(0, 3).map((todo, idx) => (
                                                        <li key={idx} className="truncate">{todo}</li>
                                                    ))}
                                                    {template.todos.length > 3 && (
                                                        <li className="text-[var(--text-muted)] italic">
                                                            ...and {template.todos.length - 3} more
                                                        </li>
                                                    )}
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="flex gap-1 ml-2">
                                    <button
                                        onClick={() => handleStartEdit(template)}
                                        className="p-2 hover:bg-[var(--button-hover-bg)] rounded-md transition-colors"
                                        title="Edit template"
                                    >
                                        <span className="material-symbols-outlined text-[20px] text-[var(--text-primary)]">
                                            edit
                                        </span>
                                    </button>
                                    <button
                                        onClick={() => handleDeleteClick(template.id, template.name)}
                                        className="p-2 hover:bg-[var(--button-hover-bg)] rounded-md transition-colors"
                                        title="Delete template"
                                    >
                                        <span className="material-symbols-outlined text-[20px] text-red-500">
                                            delete
                                        </span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {templates.length === 0 && !isCreating && !editingId && (
                <div className="text-center py-8 text-[var(--text-muted)]">
                    <span className="material-symbols-outlined text-[48px] mb-2 opacity-50">
                        inbox
                    </span>
                    <p>No templates yet. Create your first template to get started!</p>
                </div>
            )}

            {/* Confirmation Modals */}
            <ConfirmModal
                isOpen={deleteConfirm.isOpen}
                onClose={() => setDeleteConfirm({ isOpen: false, templateId: null, templateName: '' })}
                onConfirm={handleDeleteConfirm}
                title="Delete Template"
                message={`Are you sure you want to delete "${deleteConfirm.templateName}"? This action cannot be undone.`}
                confirmText="Delete"
                cancelText="Cancel"
                variant="danger"
            />

            <ConfirmModal
                isOpen={validationError.isOpen}
                onClose={() => setValidationError({ isOpen: false, message: '' })}
                onConfirm={() => setValidationError({ isOpen: false, message: '' })}
                title="Validation Error"
                message={validationError.message}
                confirmText="OK"
                cancelText={null}
                variant="primary"
            />

            <IconSelector
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleCloseIconSelector}
                onSelect={handleSelectIcon}
                currentIcon={formData.icon}
            />
        </div>
    );
};

export default ProjectTemplates;

